import PropTypes from "prop-types";

/**
 * PropTypes for QR Code Management components
 */
/**
 * Location shape for QR Code management
 *
 * @property {string} [address] - Location address
 * @property {string} [city] - Location city
 * @property {string} [documentId] - Location document ID
 * @property {Array<Object>} [evses] - EVSE list for the location
 */
const qrCodeLocationShape = PropTypes.shape({
  address: PropTypes.string,
  city: PropTypes.string,
  documentId: PropTypes.string,
  evses: PropTypes.arrayOf(PropTypes.object),
});

/**
 * Charge point row shape for QR Code management
 *
 * @property {string} [documentId] - EVSE document ID
 * @property {string} [uid] - EVSE UID
 * @property {string} [qrCodeStatus] - QR code status
 * @property {string} [qr_code_generated_at] - Generated timestamp
 * @property {string} [qr_code_url] - Public QR code URL
 * @property {string} [qr_code_image] - QR code image URL
 * @property {Object} [location] - Location info
 * @property {string} [location.address] - Location address
 * @property {string} [location.city] - Location city
 * @property {Object} [locationObject] - Location object for bulk actions
 * @property {string} ["location.address"] - Flattened location address
 * @property {string} ["location.city"] - Flattened location city
 * @property {string} ["location.documentId"] - Flattened location document ID
 */
const qrCodeChargePointShape = PropTypes.shape({
  uid: PropTypes.string,
  chargePointDocumentId: PropTypes.string,
  charge_point_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  connectors: PropTypes.arrayOf(PropTypes.object),
  connectorsCount: PropTypes.number,
  qrCodeStatus: PropTypes.oneOf(["active", "missing", "error"]),
  qr_code_generated_at: PropTypes.string,
  qr_code_url: PropTypes.string,
  qr_code_image: PropTypes.string,
  location: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
  }),
  locationObject: qrCodeLocationShape,
  "location.address": PropTypes.string,
  "location.city": PropTypes.string,
  "location.documentId": PropTypes.string,
});

/**
 * Connector row shape for QR Code management
 *
 * @property {string} [documentId] - EVSE document ID
 * @property {string} [uid] - EVSE UID
 * @property {string} [qrCodeStatus] - QR code status
 * @property {string} [qr_code_generated_at] - Generated timestamp
 * @property {string} [qr_code_url] - Public QR code URL
 * @property {string} [qr_code_image] - QR code image URL
 * @property {Object} [location] - Location info
 * @property {string} [location.address] - Location address
 * @property {string} [location.city] - Location city
 * @property {Object} [locationObject] - Location object for bulk actions
 * @property {string} ["location.address"] - Flattened location address
 * @property {string} ["location.city"] - Flattened location city
 * @property {string} ["location.documentId"] - Flattened location document ID
 */
const qrCodeConnectorShape = PropTypes.shape({
  uid: PropTypes.string,
  chargePointDocumentId: PropTypes.string,
  connector_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  qrCodeStatus: PropTypes.oneOf(["active", "missing", "error"]),
  qr_code_generated_at: PropTypes.string,
  qr_code_url: PropTypes.string,
  qr_code_image: PropTypes.string,
  location: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
  }),
  locationObject: qrCodeLocationShape,
  "location.address": PropTypes.string,
  "location.city": PropTypes.string,
  "location.documentId": PropTypes.string,
});

/**
 * PropTypes for SidebarSection component
 *
 * @property {Object|null} [selectedChargePoint] - Currently selected charge point
 * @property {Object} filters - Active filter values
 * @property {string} [filters.search] - Search term
 * @property {string} [filters.qrStatus] - QR status filter
 * @property {string} [filters.location] - Location/city filter
 * @property {function} onFilterChange - Filter change handler
 * @property {number} visibleCount - Count of EVSEs after filtering
 * @property {Array<Object>} chargePoints - Charge point list used for filters and details
 * @property {function} [onRefresh] - Refresh handler after actions
 * @property {function} [addToast] - Toast notification handler
 */
export const qrCodeSidebarSectionPropTypes = {
  selectedChargePoint: PropTypes.oneOfType([qrCodeChargePointShape, PropTypes.oneOf([null])]),
  filters: PropTypes.shape({
    search: PropTypes.string,
    qrStatus: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  visibleCount: PropTypes.number.isRequired,
  chargePointRows: PropTypes.arrayOf(qrCodeChargePointShape).isRequired,
  onRefresh: PropTypes.func,
  addToast: PropTypes.func,
};

/**
 * PropTypes for QrCodeBulkGenerateModal component
 *
 * @property {boolean} isOpen - Modal open state
 * @property {function} onClose - Close handler
 * @property {Object|null} [selectedChargePoint] - Selected charge point (for location context)
 * @property {function} [onRefresh] - Refresh handler after generation
 * @property {function} [addToast] - Toast notification handler
 */
export const qrCodeBulkGenerateModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedChargePoint: PropTypes.oneOfType([qrCodeChargePointShape, PropTypes.oneOf([null])]),
  onRefresh: PropTypes.func,
  addToast: PropTypes.func,
};

/**
 * PropTypes for QrCodeGenerateModal component
 *
 * @property {boolean} isOpen - Modal open state
 * @property {function} onClose - Close handler
 * @property {Object|null} [target] - Selected QR target
 * @property {function} [onSuccess] - Success callback after generation
 * @property {function} [onShowDetails] - Handler to open details tab
 * @property {function} [addToast] - Toast notification handler
 */
export const qrCodeGenerateModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  target: PropTypes.oneOfType([qrCodeConnectorShape, qrCodeChargePointShape, PropTypes.oneOf([null])]),
  onSuccess: PropTypes.func,
  onShowDetails: PropTypes.func,
  addToast: PropTypes.func,
};

/**
 * PropTypes for QrCodeRegenerateModal component
 *
 * @property {boolean} isOpen - Modal open state
 * @property {function} onClose - Close handler
 * @property {Object|null} [target] - Selected QR target
 * @property {function} [onSuccess] - Success callback after regeneration
 * @property {function} [addToast] - Toast notification handler
 */
export const qrCodeRegenerateModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  target: PropTypes.oneOfType([qrCodeConnectorShape, qrCodeChargePointShape, PropTypes.oneOf([null])]),
  onSuccess: PropTypes.func,
  addToast: PropTypes.func,
};

/**
 * PropTypes for QrCodeViewModal component
 *
 * @property {boolean} isOpen - Modal open state
 * @property {function} onClose - Close handler
 * @property {Object|null} [target] - Selected QR target
 * @property {function} [addToast] - Toast notification handler
 */
export const qrCodeViewModalPropTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  target: PropTypes.oneOfType([qrCodeConnectorShape, qrCodeChargePointShape, PropTypes.oneOf([null])]),
  addToast: PropTypes.func,
};
